import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useFormatterFormField } from '../../../components/FormatterSelect/FormatterSelect';
import {
  ConfigurableRendererTypeToType,
  defaultFormatterIdForRenderer,
  formatterModelsByTypeAtom,
} from '../../../components/FormatterSelect/formatter-options-state';
import { RendererType } from '../../../data-models/field-definition';
import { FieldEntity, FundReportingGridRef, GridRef } from '../../../data-models/field2.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { schemaToFormFields } from '../../../util/schema-utils';
import { CustomFieldFormType, customFieldFormSchema } from './CustomField.schema';

export function useCustomFieldsStep1Fields() {
  const { control } = useFormContext<CustomFieldFormType>();
  const rendererType = useWatch({ name: '_viewModel.rendererType', control });
  const usePresetOptions = useWatch({ name: '_viewModel.usePresetOptions', control });
  const id = useWatch({ name: 'id', control });
  const editMode = isEditMode({ id });
  const { showSecurityView, allowFreeFormMultiSelect } = useFlags<MaggieFeatureFlags>();

  const baseFields = useMemo(() => {
    return schemaToFormFields(customFieldFormSchema(showSecurityView, allowFreeFormMultiSelect), [
      'displayName',
      'description',
      '_viewModel.rendererType',
      'entity',
      'timeSeries',
    ]);
  }, [showSecurityView, allowFreeFormMultiSelect]);

  const currencyCodeField = useMemo(() => {
    const baseField = schemaToFormFields(customFieldFormSchema(), ['_viewModel.currencyConfig']).at(0)!;
    return {
      ...baseField,
      required: true,
    };
  }, []);

  const options = useWatch({ name: '_viewModel.selectConfigValues', control });
  const formatterId = useWatch({ name: '_viewModel.formatterId', control });

  // Sits directly under Type, and disappears for a Type with no configurable formatter family
  // (Text, Select, Multiselect, Color Status).
  const formatterField = useFormatterFormField({
    key: '_viewModel.formatterId',
    label: 'Format',
    renderer: rendererType as RendererType,
    value: formatterId ?? undefined,
  });

  const fieldsWithFormat = useMemo(
    () => (formatterField ? baseFields.toSpliced(3, 0, formatterField) : baseFields),
    [baseFields, formatterField]
  );

  const conditionalFields = useMemo(() => {
    // Type is at index 2; anything Type-specific goes after it and after Format when shown.
    const insertAt = formatterField ? 4 : 3;
    if (rendererType === RendererType.singleSelect) {
      return fieldsWithFormat.toSpliced(insertAt, 0, getOptionsField(options, true));
    }
    if (rendererType === RendererType.multiSelect) {
      if (!allowFreeFormMultiSelect) {
        return fieldsWithFormat.toSpliced(insertAt, 0, getOptionsField(options, true));
      }
      const presetOptionsToggle = schemaToFormFields(customFieldFormSchema(), [
        '_viewModel.usePresetOptions',
      ]).at(0)!;
      if (usePresetOptions) {
        return fieldsWithFormat.toSpliced(insertAt, 0, presetOptionsToggle, getOptionsField(options, true));
      }
      return fieldsWithFormat.toSpliced(insertAt, 0, presetOptionsToggle);
    }
    if (rendererType === RendererType.currency) {
      return fieldsWithFormat.toSpliced(insertAt, 0, currencyCodeField);
    }
    return fieldsWithFormat;
  }, [
    allowFreeFormMultiSelect,
    currencyCodeField,
    fieldsWithFormat,
    formatterField,
    options,
    rendererType,
    usePresetOptions,
  ]);

  return useMemo(
    () =>
      conditionalFields.map((field) => {
        if (editMode && disabledForEdit.has(field.key as FieldPath<CustomFieldFormType>)) {
          return { ...field, disabled: true };
        }
        return field;
      }),
    [conditionalFields, editMode]
  );
}

function getOptionsField(options: (string | undefined)[] | null | undefined, required: boolean) {
  const optionsField = schemaToFormFields(customFieldFormSchema(), ['_viewModel.selectConfigValues']).at(0)!;
  optionsField.rendererMeta = {
    ...(optionsField.rendererMeta ?? {}),
    values: options?.map((option) => ({
      displayName: option,
      value: option,
    })),
  };
  optionsField.required = required;
  return optionsField;
}

export function useCustomFieldsStep2Fields(entity: FieldEntity) {
  const getConditionalFields = useDisplayFields();

  return useMemo(() => {
    return schemaToFormFields(customFieldFormSchema(), getConditionalFields(entity as FieldEntity));
  }, [entity, getConditionalFields]);
}

export function useCustomFieldFormDynamicValues() {
  const { watch, setValue, control } = useFormContext<CustomFieldFormType>();
  const rendererType = useWatch({ name: '_viewModel.rendererType', control }) as RendererType;
  const formatterId = useWatch({ name: '_viewModel.formatterId', control });
  const formatterModelsByType = useAtomValue(formatterModelsByTypeAtom);

  const offeredFormatterIdsFor = useCallback(
    (renderer: RendererType | string | undefined) => {
      const family = ConfigurableRendererTypeToType[renderer as RendererType];
      return new Set((family ? (formatterModelsByType.get(family) ?? []) : []).map((model) => model.id));
    },
    [formatterModelsByType]
  );

  /** The format a Type should start on, or null when it has none the picker can offer. */
  const preferredFormatterIdFor = useCallback(
    (renderer: RendererType | string | undefined) => {
      const preferred = defaultFormatterIdForRenderer(renderer);
      return preferred && offeredFormatterIdsFor(renderer).has(preferred) ? preferred : null;
    },
    [offeredFormatterIdsFor]
  );

  /**
   * Keeps Format on something the picker can actually show. A field stamped with the
   * unregistered `number` id — every custom Number field — otherwise opened with the control
   * blank, and a fresh Type had nothing selected at all.
   *
   * `null` rather than `undefined`: react-hook-form reads an undefined nested value through to
   * the form's initial values, so the stale id would come back and be re-cleared every render.
   */
  useEffect(() => {
    if (formatterId && offeredFormatterIdsFor(rendererType).has(formatterId)) {
      return;
    }
    const next = preferredFormatterIdFor(rendererType);
    if (next !== (formatterId ?? null)) {
      setValue('_viewModel.formatterId', next);
    }
  }, [formatterId, offeredFormatterIdsFor, preferredFormatterIdFor, rendererType, setValue]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change') {
        return;
      }
      if (name === '_viewModel.rendererType') {
        setValue('_viewModel.selectConfigValues', undefined);
        setValue('_viewModel.currencyConfig', undefined);
        setValue('_viewModel.usePresetOptions', false);
        // Currency and Number share a formatter family, so the old id can still be offered under
        // the new Type. A format belongs to the Type that offered it, so start from its default
        // rather than leaving, say, a currency format on a plain number field. Goes through the
        // same checked helper as the effect above, so both land on an offered id or null.
        setValue('_viewModel.formatterId', preferredFormatterIdFor(value._viewModel?.rendererType));
      }
      if (name === '_viewModel.usePresetOptions' && !value._viewModel?.usePresetOptions) {
        setValue('_viewModel.selectConfigValues', undefined);
      }
    });
    return () => subscription.unsubscribe();
  }, [preferredFormatterIdFor, setValue, watch]);
}

function useDisplayFields() {
  const canViewDeals = PermissionService.get().hasPermission(PermissionKey.canViewDeal);
  const canViewList = PermissionService.get().hasPermission(PermissionKey.canViewList);
  const canViewDataCollection = PermissionService.get().hasPermission(PermissionKey.hasDataCollectionModule);

  return useCallback(
    (entity: FieldEntity) => {
      let result: FieldPath<CustomFieldFormType>[] = [];
      if (entity === FieldEntity.company) {
        result = [
          `_viewModel.addingTransaction`,
          `_viewModel.companyProfile`,

          `_viewModel.${GridRef.portfolioReporting}`,
          `_viewModel.${GridRef.roundTracker}`,
        ];
      } else if (entity === FieldEntity.fundCompany) {
        result = [`_viewModel.companyProfile`, `_viewModel.${GridRef.portfolioReporting}`];
      } else if (entity === FieldEntity.round) {
        result = [
          `_viewModel.addingRound`,
          `_viewModel.${GridRef.portfolioReporting}`,
          `_viewModel.${GridRef.roundTracker}`,
        ];
      } else if (entity === FieldEntity.captableInvestment) {
        result = [`_viewModel.addingCaptableSecurity`, `_viewModel.${GridRef.securityOverview}`];
      } else if (entity === FieldEntity.fund) {
        result = [`_viewModel.${FundReportingGridRef}`];
      }

      if (canViewDeals && (entity === FieldEntity.company || entity === FieldEntity.deal)) {
        result.unshift(`_viewModel.addingDeal`);
      }
      if (canViewList && entity === FieldEntity.company) {
        const idx = canViewDeals ? 3 : 2;
        result.splice(idx, 0, `_viewModel.companyLists`);
      }
      if (canViewDataCollection && entity === FieldEntity.company) {
        result.push(`_viewModel.${GridRef.kpiDataCollection}`);
      }
      return result;
    },
    [canViewDeals, canViewList, canViewDataCollection]
  );
}

export const disabledForEdit = new Set<FieldPath<CustomFieldFormType>>([
  `_viewModel.rendererType`,
  `_viewModel.usePresetOptions`,
  `entity`,
]);

export function isEditMode({ id }: { id?: number | null | undefined }) {
  return id != null;
}
